import { toast } from 'sonner';
import { useBoolean } from 'minimal-shared/hooks';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { getUserDisplayName } from 'src/utils/commonUtils';
import {
  addAuditFields,
  convertEmptyToNull,
  extractCustomFields,
  buildAssignedPayload,
} from 'src/utils/form-helpers';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { useHandleForm } from 'src/hook/useHandleForm';
import { useAssigneeField } from 'src/hook/use-assignee-field';
import { type IAccount, defaultAccount } from 'src/pages/accounts/accounts.types';
import {
  useCreateAccountMutation,
  useGetAccountDtlMutation,
} from 'src/pages/accounts/slice/accountsApi';

import { useFilterLifecycleMutation } from '../../setting/components/lifecycle/slice/lifecycleApi';

import type { IContact, IContactCreate } from '../contacts.types';

// ----------------------------------------------------------------------

interface UseContactFormProps {
  open: boolean;
  onClose: () => void;
  selectedUser?: IContact;
  handleSubmitData: (data: any, assignedPayload: any) => void;
  customFields: any[] | [];
}

export function useContactForm({
  open,
  onClose,
  selectedUser,
  handleSubmitData,
  customFields,
}: UseContactFormProps) {
  const { t } = useTranslate('translation');
  const { loginApiData } = useAppSelector((state) => (state as any).auth);

  // Mutations
  const [getAccountDtl] = useGetAccountDtlMutation();
  const [createAccount] = useCreateAccountMutation();
  const [getLifecycleFields] = useFilterLifecycleMutation();

  // State
  const [stages, setStages] = useState<any[]>([]);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<IAccount | undefined>(undefined);

  const accountMenuDialog = useBoolean();

  // Handlers
  const fetchLifecycleStages = useCallback(async () => {
    try {
      setStagesLoading(true);
      const res = await getLifecycleFields({}).unwrap();
      setStages(res?.list || []);
    } catch (error) {
      console.error('Error fetching lifecycle stages:', error);
      toast.error(t('LIFECYCLE_FAIL'));
    } finally {
      setStagesLoading(false);
    }
  }, [getLifecycleFields, t]);

  // useEffect(() => {
  //   if (open) {
  //     fetchLifecycleStages();
  //   }
  // }, [open, fetchLifecycleStages]);

  // Handle Form
  const fldList = [
    { name: 'firstName', required: false },
    { name: 'lastName', required: true },
    { name: 'email', required: false },
    { name: 'phone', required: false },
    { name: 'address', required: false },
    { name: 'city', required: false },
    { name: 'nationalId', required: false },
    { name: 'country', required: false },
    { name: 'companyId', required: false },
    { name: 'assignUsers', required: true },
    { name: 'lifecycleStage', required: false },
  ];

  const { formKey, ...methods } = useHandleForm({
    isDialogOpen: open,
    module: 'contact',
    fldList,
    customFields,
    selectedRecord: selectedUser,
  });

  const { handleSubmit, setValue, reset } = methods;

  // Account Logic
  const initialAccDtl = useMemo(() => {
    if (selectedUser?.company?.id && selectedUser?.company?.name) {
      return { label: selectedUser.company.name, value: selectedUser.company.id };
    }
    // Fallback if the above isn't populated but accountName is (from our transform)
    if (selectedUser?.companyId && (selectedUser as any).accountName) {
      return { label: (selectedUser as any).accountName, value: selectedUser.companyId };
    }
    return null;
  }, [selectedUser]);

  const [selectedUserAccDtl, setSelectedUserAccDtl] = useState<{
    label: string;
    value: string;
  } | null>(initialAccDtl);

  useEffect(() => {
    if (open) {
      setSelectedUserAccDtl(initialAccDtl);
    }
  }, [open, initialAccDtl]);

  useEffect(() => {
    // Overwrite with fresh details if needed, but only for labels
    if (open && selectedUser?.companyId && !selectedUserAccDtl?.label) {
      getAccountDtl(selectedUser.companyId)
        .unwrap()
        .then((res) => {
          const companyDtl = res?.data?.company ?? null;
          if (companyDtl) {
            setSelectedUserAccDtl({ label: companyDtl.name || '', value: companyDtl.id || '' });
          }
        })
        .catch(() => {
          console.error('Failed to fetch account for label');
        });
    }
  }, [open, selectedUser, getAccountDtl, selectedUserAccDtl]);
  const userName = useMemo(() => getUserDisplayName(loginApiData), [loginApiData]);

  const handleCreateAccount = () => {
    setSelectedAccount({
      ...defaultAccount,
      assignedId: loginApiData && 'id' in loginApiData && loginApiData?.id,
      assignedType: 'USER',
      CompanyAssign: [
        {
          ...loginApiData,
          name: userName,
          assignedType: 'USER',
        },
      ],
    });
    accountMenuDialog.onTrue();
  };

  const handleCreateAccSubmit = async (account: Partial<IAccount>) => {
    delete account.id;
    try {
      const res = await createAccount(convertEmptyToNull(account)).unwrap();
      if (res) {
        toast.success(t('ACCOUNT_CREATED_SUCCESSFULLY'));
        setValue('companyId', res?.id || ''); // Set the ID, not the name
        setSelectedUserAccDtl({ label: res?.name || '', value: res?.id || '' });
      }
    } catch (err) {
      toast.error(t('ACCOUNT_CREATION_FAILED'));
    } finally {
      accountMenuDialog.onFalse();
      setSelectedAccount(undefined);
    }
  };

  const { assigneeProps } = useAssigneeField({
    multiFieldAssign: selectedUser?.ContactAssign,
    setValue: setValue as any,
  });

  const onSubmit = handleSubmit(async (data: Partial<IContactCreate>) => {
    const isEdit = !!selectedUser?.id;
    const auditFields = addAuditFields(isEdit, loginApiData?.id);
    const assignedPayload = buildAssignedPayload(data.assignUsers);
    const customFieldsData = extractCustomFields(data);

    // Extract core fields and exclude custom fields and internal fields
    const { assignUsers, lifecycleStage, ...rest } = data;

    const updatedData = {
      firstName: rest.firstName ?? '',
      lastName: rest.lastName ?? '',
      email: rest.email ?? '',
      phone: rest.phone ? rest.phone.replace(/\+/g, '') : '',
      address: rest.address ?? '',
      city: rest.city ?? '',
      nationalId: rest.nationalId ?? '',
      country: rest.country ?? '',
      lifecycleStageId: lifecycleStage || null,
      companyId: selectedUserAccDtl?.value || null,
      customFields: customFieldsData,
      ...auditFields,
      ...(isEdit && selectedUser?.id ? { id: selectedUser.id, name: selectedUser.name } : {}),
    };

    handleSubmitData(updatedData, assignedPayload);
  });

  const handleClose = () => {
    setSelectedUserAccDtl(null);
    onClose();
    reset();
  };

  return {
    formKey,
    methods,
    onSubmit,
    handleClose,
    // Stages
    stages,
    stagesLoading,
    // Account logic
    accountMenuDialog,
    selectedAccount,
    selectedUserAccDtl,
    setSelectedUserAccDtl,
    handleCreateAccount,
    handleCreateAccSubmit,
    // Assignees
    assigneeProps,
    t,
  };
}
